import { useEffect, useState } from "react";
import { Button, Input, Table, Thead, Tbody, Tr, Th, Td, Spinner, Box, HStack, useBreakpointValue } from '@chakra-ui/react';
import { SiGmail, SiGooglemeet } from "react-icons/si";
import { FaSquareWhatsapp } from "react-icons/fa6";
import { toast } from "react-toastify";
import { getApi } from "services/api";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import GmailIcon from "./GmailIcon";

const Index = () => {
  const param = useParams();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [emailData, setEmailData] = useState([]);
  const [meetingData, setMeetingData] = useState([]);
  const [whatsappData, setWhatsappData] = useState([]);

  // Retrieve logged-in user details
  const user = JSON.parse(localStorage.getItem("user"));
  // //console.log("user",user);

  const fetchEmailData = async () => {
    setIsLoading(true);
    try {
      const response = await getApi("api/email");

      // //console.log("maildata",response);
      if (response.status === 200) {
        const filteredEmails = response.data.filter(
          (mail) => mail.sender === user._id
        );

        const formattedData = filteredEmails.map((mail) => ({
          type: "email",
          name: mail.senderName || "Unknown",
          details: `${mail.subject} (To: ${mail.recipient})`,
          timestamp: mail.timestamp,
        }));
        setEmailData(formattedData);
      }
    } catch (error) {
      toast.error("Error fetching email data");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMeetingData = async () => {
    setIsLoading(true);
    try {
      const response = await getApi("api/meeting");
      // //console.log("Meeting Data",response);
      if (response.status === 200) {
        // Show meetings created by the logged-in user
        const filteredMeetings = response.data.filter(
          (meeting) => meeting.createBy === user._id
        );

        const formattedData = filteredMeetings.map((meeting) => ({
          type: "meeting",
          name: meeting.createdByName,
          details: `${meeting.notes} (Location: ${meeting.location})`,
          timestamp: meeting.timestamp,
        }));
        setMeetingData(formattedData);
      }
    } catch (error) {
      toast.error("Error fetching meeting data");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchWhatsappData = async () => {
    setIsLoading(true);
    try {
      const response = await getApi("api/whatsapp");
      // //console.log("Whatsapp",response.data.data);
      if (response.status === 200) {
        const filteredWhatsapp = response.data.data.filter(
          (whatsapp) =>
            whatsapp.createBy._id === user._id
        );

        const formattedData = filteredWhatsapp.map((whatsapp) => ({
          type: "whatsapp",
          name: whatsapp.sender,
          details: `${whatsapp.recipient} (${whatsapp.recipientNumber})`,
          timestamp: whatsapp.startDate,
        }));
        setWhatsappData(formattedData);
      }
    } catch (error) {
      toast.error("Error fetching WhatsApp data");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEmailData();
    fetchMeetingData();
    fetchWhatsappData();
  }, []);

  const showFullText = useBreakpointValue({ base: false, md: true });
  const allData = [...emailData, ...meetingData, ...whatsappData].sort(
    (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
  );

  const filteredData = (
    filterType === "all"
      ? allData
      : filterType === "email"
      ? emailData
      : filterType === "meeting"
      ? meetingData
      : whatsappData
  ).filter(
    (item) =>
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.details.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box p={4} bg="white" maxWidth="100vw">
      <HStack justify="flex-end" mb={4} spacing={4}>
        <Button colorScheme="gray" onClick={() => setFilterType("all")}>
          {showFullText ? `All (${allData.length})` : "All"}
        </Button>
        <Button colorScheme="blue" onClick={() => setFilterType("email")}>
          <GmailIcon size={24} style={{ marginRight: "3px" }} />
          {showFullText && ` Email (${emailData.length})`}
        </Button>
        <Button colorScheme="green" onClick={() => setFilterType("whatsapp")}>
          <FaSquareWhatsapp size={34} style={{ marginRight: "3px" }} />
          {showFullText && ` WhatsApp (${whatsappData.length})`}
        </Button>
        <Button colorScheme="red" onClick={() => setFilterType("meeting")}>
          <SiGooglemeet size={28} style={{ marginRight: "4px" }} />
          {showFullText && ` Meeting (${meetingData.length})`}
        </Button>
      </HStack>

      <Input
        type="text"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder="Search..."
        size="md"
        mb={4}
      />

      <Box overflowX="auto">
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Activity</Th>
              <Th>Sender</Th>
              <Th>Details</Th>
              <Th>Timestamp</Th>
            </Tr>
          </Thead>
          <Tbody>
            {isLoading ? (
              <Tr>
                <Td colSpan={4}>
                  <Spinner />
                </Td>
              </Tr>
            ) : filteredData.length > 0 ? (
              filteredData.map((item, index) => (
                <Tr key={index}>
                  <Td>
                    {item.type === "email" ? (
                      <GmailIcon />
                    ) : item.type === "meeting" ? (
                      <SiGooglemeet
                        size={28}
                        style={{ color: "red", marginLeft: "10px" }}
                      />
                    ) : (
                      <FaSquareWhatsapp
                        size={34}
                        style={{ color: "green", marginLeft: "6px" }}
                      />
                    )}
                  </Td>
                  <Td>{item.name}</Td>
                  <Td>{item.details}</Td>
                  <Td>{new Date(item.timestamp).toLocaleString()}</Td>
                </Tr>
              ))
            ) : (
              <Tr>
                <Td colSpan={4}>No data found</Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

export default Index;
